"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MetadataModule = void 0;
var tslib_1 = require("tslib");
// (C) 2007-2021 GoodData Corporation
var isPlainObject_1 = tslib_1.__importDefault(require("lodash/isPlainObject"));
var chunk_1 = tslib_1.__importDefault(require("lodash/chunk"));
var flatten_1 = tslib_1.__importDefault(require("lodash/flatten"));
var pick_1 = tslib_1.__importDefault(require("lodash/pick"));
var pickBy_1 = tslib_1.__importDefault(require("lodash/pickBy"));
var api_model_bear_1 = require("@gooddata/api-model-bear");
var util_1 = require("./util");
var xhr_1 = require("./xhr");
var queryString_1 = require("./utils/queryString");
function getResponseData(r) {
    if (!r.response.ok) {
        throw new xhr_1.ApiResponseError(r.response.statusText, r.response, r.getData());
    }
    return r.getData();
}
/**
 * Functions for working with metadata objects
 *
 */
var MetadataModule = /** @class */ (function () {
    function MetadataModule(xhr) {
        this.xhr = xhr;
    }
    /**
     * Get default display form value of provided attribute element uri
     * @param attributeElementUri - string
     */
    MetadataModule.prototype.getAttributeElementDefaultDisplayFormValue = function (attributeElementUri) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var uriChunks, attributeUri, elementId, defaultDisplayForm, defaultDisplayFormUri, defaultDisplayFormElementValue;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        uriChunks = attributeElementUri.match(/(.+)\/elements\?id=(.*)/);
                        if (!uriChunks) {
                            throw new Error("Provide valid attribute element uri");
                        }
                        attributeUri = uriChunks[1];
                        elementId = uriChunks[2];
                        return [4 /*yield*/, this.getAttributeDefaultDisplayForm(attributeUri)];
                    case 1:
                        defaultDisplayForm = _a.sent();
                        if (!defaultDisplayForm) {
                            throw new Error("Attribute of the provided element has no default display form!");
                        }
                        defaultDisplayFormUri = defaultDisplayForm.meta.uri;
                        return [4 /*yield*/, this.xhr.getParsed(defaultDisplayFormUri + "/elements?id=" + elementId)];
                    case 2:
                        defaultDisplayFormElementValue = _a.sent();
                        return [2 /*return*/, defaultDisplayFormElementValue.attributeElements.elements[0]];
                }
            });
        });
    };
    /**
     * Get default display form of provided attribute uri
     * @param attributeUri - string
     */
    MetadataModule.prototype.getAttributeDefaultDisplayForm = function (attributeUri) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var object;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.xhr.getParsed(attributeUri)];
                    case 1:
                        object = _a.sent();
                        if (!api_model_bear_1.GdcMetadata.isWrappedAttribute(object)) {
                            throw new Error("Provided uri is not attribute uri!");
                        }
                        return [2 /*return*/, (object.attribute.content.displayForms.find(function (displayForm) { return displayForm.content.default === 1; }) ||
                                object.attribute.content.displayForms[0])];
                }
            });
        });
    };
    /**
     * Get metadata object by provided identifier
     * @param projectId - string
     * @param identifier - string
     */
    MetadataModule.prototype.getObjectByIdentifier = function (projectId, identifier) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var uri;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.getObjectUri(projectId, identifier)];
                    case 1:
                        uri = _a.sent();
                        return [2 /*return*/, this.xhr.getParsed(uri)];
                }
            });
        });
    };
    /**
     * Get metadata objects by provided identifiers
     * @param projectId - string
     * @param identifiers - string[]
     */
    MetadataModule.prototype.getObjectsByIdentifiers = function (projectId, identifiers) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var uriIdentifierPairs, uris;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.getUrisFromIdentifiers(projectId, identifiers)];
                    case 1:
                        uriIdentifierPairs = _a.sent();
                        uris = uriIdentifierPairs.map(function (pair) { return pair.uri; });
                        return [4 /*yield*/, this.getObjects(projectId, uris)];
                    case 2: return [2 /*return*/, _a.sent()];
                }
            });
        });
    };
    /**
     * Load all objects with given uris
     * (use bulk loading instead of getting objects one by one)
     *
     * @param projectId - id of the project
     * @param objectUris - array of uris for objects to be loaded
     * @returns array of loaded elements
     */
    MetadataModule.prototype.getObjects = function (projectId, objectUris) {
        var _this = this;
        var LIMIT = 50;
        var uri = "/gdc/md/" + projectId + "/objects/get";
        var objectsUrisChunks = chunk_1.default(objectUris, LIMIT);
        var promises = objectsUrisChunks.map(function (objectUrisChunk) {
            var body = {
                get: {
                    items: objectUrisChunk,
                },
            };
            return _this.xhr
                .post(uri, { body: body })
                .then(function (r) {
                if (!r.response.ok) {
                    throw new xhr_1.ApiResponseError(r.response.statusText, r.response, r.responseBody);
                }
                return r.getData();
            })
                .then(function (result) {
                return result.objects.items.map(function (item) {
                    if (item.visualizationObject) {
                        return {
                            visualizationObject: item.visualizationObject,
                        };
                    }
                    return item;
                });
            });
        });
        return Promise.all(promises).then(flatten_1.default);
    };
    /**
     * Loads all objects by query (fetches all pages, one by one)
     *
     * @param projectId - id of the project
     * @param options - (see https://developer.gooddata.com/api endpoint: `/gdc/md/{project_id}/objects/query`)
     *        - category - for example 'dataSets' or 'projectDashboard'
     *        - mode - 'enriched' or 'raw'
     *        - author - the URI of the author of the metadata objects
     *        - limit - default is 50 (also maximum)
     *        - deprecated - show also deprecated objects
     * @returns array of returned objects
     */
    MetadataModule.prototype.getObjectsByQuery = function (projectId, options) {
        var _this = this;
        var getOnePage = function (uri, items) {
            if (items === void 0) { items = []; }
            return _this.xhr
                .get(uri)
                .then(function (r) { return r.getData(); })
                .then(function (_a) {
                var objects = _a.objects;
                items.push.apply(items, objects.items);
                var nextUri = objects.paging.next;
                return nextUri ? getOnePage(nextUri, items) : items;
            });
        };
        var deprecated = options.deprecated ? { deprecated: 1 } : {};
        var uri = "/gdc/md/" + projectId + "/objects/query";
        var query = pick_1.default(tslib_1.__assign(tslib_1.__assign({ limit: 50 }, options), deprecated), [
            "category",
            "mode",
            "author",
            "limit",
            "deprecated",
        ]);
        return getOnePage(uri + util_1.queryString(query));
    };
    /**
     * Loads all objects by query with paging
     *
     * @param projectId - id of the project
     * @param options - (see https://developer.gooddata.com/api endpoint: `/gdc/md/{project_id}/objects/query`)
     *        - category - for example 'dataSets' or 'projectDashboard'
     *        - mode - 'enriched' or 'raw'
     *        - author - the URI of the author of the metadata objects
     *        - limit - default is 50 (also maximum)
     *        - deprecated - show also deprecated objects
     *        - orderBy - order the results by id, title or the last updated (newest first)
     *        - getTotalCount - include total count of items in the paging object
     * @returns array of returned objects
     */
    MetadataModule.prototype.getObjectsByQueryWithPaging = function (projectId, options) {
        var getTotalCount = options && options.getTotalCount ? 1 : 0;
        var uri = "/gdc/md/" + projectId + "/objects/query?" + queryString_1.stringify(tslib_1.__assign(tslib_1.__assign({}, options), { getTotalCount: getTotalCount }));
        return this.xhr
            .get(uri)
            .then(function (r) { return r.getData(); })
            .then(function (_a) {
            var objects = _a.objects;
            return objects;
        });
    };
    /**
     * Get MD objects from using2 resource. Include only objects of given types
     * and take care about fetching only nearest objects if requested.
     *
     * @param projectId - id of the project
     * @param uri - uri of the object for which dependencies are to be found
     * @param options - objects with options:
     *        - types - array of strings with object types to be included
     *        - nearest - whether to include only nearest dependencies
     * @returns promise promise once resolved returns an array of
     *         entries returned by using2 resource
     */
    MetadataModule.prototype.getObjectUsing = function (projectId, uri, options) {
        if (options === void 0) { options = {}; }
        var _a = options.types, types = _a === void 0 ? [] : _a, _b = options.nearest, nearest = _b === void 0 ? false : _b;
        var resourceUri = "/gdc/md/" + projectId + "/using2";
        var body = {
            inUse: {
                uri: uri,
                types: types,
                nearest: nearest ? 1 : 0,
            },
        };
        return this.xhr
            .post(resourceUri, { body: body })
            .then(getResponseData)
            .then(function (result) { return result.entries; });
    };
    /**
     * Get MD objects from using2 resource. Include only objects of given types
     * and take care about fetching only nearest objects if requested.
     *
     * @param projectId - id of the project
     * @param uris - uris of objects for which dependencies are to be found
     * @param options - objects with options:
     *        - types - array of strings with object types to be included
     *        - nearest - whether to include only nearest dependencies
     * @returns promise promise once resolved returns an array of
     *         entries returned by using2 resource
     */
    MetadataModule.prototype.getObjectUsingMany = function (projectId, uris, options) {
        if (options === void 0) { options = {}; }
        var _a = options.types, types = _a === void 0 ? [] : _a, _b = options.nearest, nearest = _b === void 0 ? false : _b;
        var resourceUri = "/gdc/md/" + projectId + "/using2";
        var body = {
            inUseMany: {
                uris: uris,
                types: types,
                nearest: nearest ? 1 : 0,
            },
        };
        return this.xhr
            .post(resourceUri, { body: body })
            .then(getResponseData)
            .then(function (result) { return result.useMany; });
    };
    /**
     * Get MD objects from usedby2 resource. Include only objects of given types
     * and take care about fetching only nearest objects if requested.
     *
     * @param projectId - id of the project
     * @param uri - uri of the object for which dependencies are to be found
     * @param options - objects with options:
     *        - types - array of strings with object types to be included
     *        - nearest - whether to include only nearest dependencies (default is false)
     * @returns promise promise once resolved returns an array of
     *         entries returned by usedby2 resource
     */
    MetadataModule.prototype.getObjectUsedBy = function (projectId, uri, options) {
        var _a = options.nearest, nearest = _a === void 0 ? false : _a, _b = options.types, types = _b === void 0 ? [] : _b;
        var body = {
            inUse: {
                nearest: nearest ? 1 : 0,
                uri: uri,
                types: types,
            },
        };
        return this.xhr
            .postParsed("/gdc/md/" + projectId + "/usedby2", { body: body })
            .then(function (result) { return result.entries; });
    };
    /**
     * Get MD objects from usedby2 resource. Include only objects of given types
     * and take care about fetching only nearest objects if requested.
     *
     * @param projectId - id of the project
     * @param uris - uris of objects for which dependencies are to be found
     * @param options - objects with options:
     *        - types - array of strings with object types to be included
     *        - nearest - whether to include only nearest dependencies (default is false)
     * @returns promise promise once resolved returns an array of
     *         entries returned by usedby2 resource
     */
    MetadataModule.prototype.getObjectsUsedByMany = function (projectId, uris, options) {
        var uri = "/gdc/md/" + projectId + "/usedby2";
        var _a = options.nearest, nearest = _a === void 0 ? false : _a, _b = options.types, types = _b === void 0 ? [] : _b;
        var body = {
            inUseMany: {
                nearest: nearest ? 1 : 0,
                uris: uris,
                types: types,
            },
        };
        return this.xhr
            .postParsed(uri, { body: body })
            .then(function (result) { return result.useMany; });
    };
    /**
     * Returns all visualizationObjects metadata in a project specified by projectId param
     *
     * @param projectId - Project identifier
     * @returns An array of visualization objects metadata
     */
    MetadataModule.prototype.getVisualizations = function (projectId) {
        return this.xhr
            .get("/gdc/md/" + projectId + "/query/visualizationobjects")
            .then(function (apiResponse) {
            return apiResponse.response.ok ? apiResponse.getData() : apiResponse.response;
        })
            .then(util_1.getQueryEntries);
    };
    /**
     * Returns all attributes in a project specified by projectId param
     *
     * @param projectId - Project identifier
     * @returns An array of attribute objects
     */
    MetadataModule.prototype.getAttributes = function (projectId) {
        return this.xhr
            .get("/gdc/md/" + projectId + "/query/attributes")
            .then(function (apiResponse) {
            return apiResponse.response.ok ? apiResponse.getData() : apiResponse.response;
        })
            .then(util_1.getQueryEntries);
    };
    /**
     * Returns all dimensions in a project specified by projectId param
     *
     * @param projectId - Project identifier
     * @returns An array of dimension objects
     * @see getFolders
     */
    MetadataModule.prototype.getDimensions = function (projectId) {
        return this.xhr
            .get("/gdc/md/" + projectId + "/query/dimensions")
            .then(function (apiResponse) {
            return apiResponse.response.ok ? apiResponse.getData() : apiResponse.response;
        })
            .then(util_1.getQueryEntries);
    };
    /**
     * Returns project folders. Folders can be of specific types and you can specify
     * the type you need by passing and optional `type` parameter
     *
     * @param projectId - Project identifier
     * @param type - Optional, possible values are `metric`, `fact`, `attribute`
     * @returns An array of dimension objects
     */
    MetadataModule.prototype.getFolders = function (projectId, type) {
        var _this = this;
        // TODO enum?
        var getFolderEntries = function (pId, t) {
            var typeURL = t ? "?type=" + t : "";
            return _this.xhr
                .get("/gdc/md/" + pId + "/query/folders" + typeURL)
                .then(function (r) { return r.getData(); })
                .then(util_1.getQueryEntries);
        };
        switch (type) {
            case "fact":
            case "metric":
                return getFolderEntries(projectId, type);
            case "attribute":
                return this.getDimensions(projectId);
            default:
                return Promise.all([
                    getFolderEntries(projectId, "fact"),
                    getFolderEntries(projectId, "metric"),
                    this.getDimensions(projectId),
                ]).then(function (_a) {
                    var fact = _a[0], metric = _a[1], attribute = _a[2];
                    return { fact: fact, metric: metric, attribute: attribute };
                });
        }
    };
    /**
     * Returns all facts in a project specified by the given projectId
     *
     * @param projectId - Project identifier
     * @returns An array of fact objects
     */
    MetadataModule.prototype.getFacts = function (projectId) {
        return this.xhr
            .get("/gdc/md/" + projectId + "/query/facts")
            .then(function (apiResponse) {
            return apiResponse.response.ok ? apiResponse.getData() : apiResponse.response;
        })
            .then(util_1.getQueryEntries);
    };
    /**
     * Returns all metrics in a project specified by the given projectId
     *
     * @param projectId - Project identifier
     * @returns An array of metric objects
     */
    MetadataModule.prototype.getMetrics = function (projectId) {
        return this.xhr
            .get("/gdc/md/" + projectId + "/query/metrics")
            .then(function (apiResponse) {
            return apiResponse.response.ok ? apiResponse.getData() : apiResponse.response;
        })
            .then(util_1.getQueryEntries);
    };
    /**
     * Returns all project dashboards (pixel perfect dashboards) in a project specified by the given projectId
     *
     * @param projectId - Project identifier
     * @returns An array of project dashboard objects
     */
    MetadataModule.prototype.getProjectDashboards = function (projectId) {
        var _this = this;
        return this.xhr
            .getParsed("/gdc/md/" + projectId + "/query/projectdashboards")
            .then(function (dashboardsQuery) {
            var dashboardLinks = dashboardsQuery.query.entries.map(function (dashboard) { return dashboard.link; });
            return _this.getObjects(projectId, dashboardLinks);
        });
    };
    /**
     * Returns all analytical dashboards (kpi dashboards) in a project specified by the given projectId
     *
     * @param projectId - Project identifier
     * @param fetchAllListedDashboards - Optionally specify if also all the listed
     *  dashboards should be loaded. Note that these include not just shared dashboards and dashboards
     *  that were not shared with the user but are accessible via link, but also dashboards that cannot
     *  be accessed because there were not shared are under strict control access (only its listed record
     *  is accessible, not the whole metadata object).
     * @returns An array of links to analytical dashboard objects
     */
    MetadataModule.prototype.getAnalyticalDashboards = function (projectId, fetchAllListedDashboards) {
        return this.xhr
            .getParsed("/gdc/md/" + projectId + "/query/analyticaldashboard?showAll=" + (fetchAllListedDashboards ? 1 : 0))
            .then(function (dashboardsQuery) {
            return dashboardsQuery.query.entries;
        });
    };
    /**
     * Returns all dashboard plugins in a project specified by the given projectId
     *
     * @param projectId - Project identifier
     * @returns An array of links to dashboard plugin objects
     */
    MetadataModule.prototype.getDashboardPlugins = function (projectId) {
        return this.xhr
            .getParsed("/gdc/md/" + projectId + "/query/dashboardplugins")
            .then(function (dashboardsQuery) {
            return dashboardsQuery.query.entries;
        });
    };
    /**
     * Returns all metrics that are reachable (with respect to ldm of the project
     * specified by the given projectId) for given attributes
     *
     * @param projectId - Project identifier
     * @param attrs - An array of attribute uris for which we want to get
     * available metrics
     * @returns An array of reachable metrics for the given attrs
     * @see getAvailableAttributes
     * @see getAvailableFacts
     */
    MetadataModule.prototype.getAvailableMetrics = function (projectId, attrs) {
        if (attrs === void 0) { attrs = []; }
        return this.xhr
            .post("/gdc/md/" + projectId + "/availablemetrics", { body: attrs })
            .then(function (apiResponse) {
            return apiResponse.response.ok ? apiResponse.getData() : apiResponse.response;
        })
            .then(function (data) { return data.entries; });
    };
    /**
     * Returns all attributes that are reachable (with respect to ldm of the project
     * specified by the given projectId) for given metrics (also called as drillCrossPath)
     *
     * @param projectId - Project identifier
     * @param metrics - An array of metric uris for which we want to get
     * available attributes
     * @returns An array of reachable attributes for the given metrics
     * @see getAvailableMetrics
     * @see getAvailableFacts
     */
    MetadataModule.prototype.getAvailableAttributes = function (projectId, metrics) {
        if (metrics === void 0) { metrics = []; }
        return this.xhr
            .post("/gdc/md/" + projectId + "/drillcrosspaths", { body: metrics })
            .then(function (apiResponse) { return (apiResponse.response.ok ? apiResponse.getData() : apiResponse.response); })
            .then(function (r) { return r.drillcrosspath.links; });
    };
    /**
     * Returns all attributes that are reachable (with respect to ldm of the project
     * specified by the given projectId) for given metrics (also called as drillCrossPath)
     *
     * @param projectId - Project identifier
     * @param items - An array of metric or attribute uris for which we want to get
     * available facts
     * @returns An array of reachable facts for the given items
     * @see getAvailableAttributes
     * @see getAvailableMetrics
     */
    MetadataModule.prototype.getAvailableFacts = function (projectId, items) {
        if (items === void 0) { items = []; }
        return this.xhr
            .post("/gdc/md/" + projectId + "/availablefacts", { body: items })
            .then(function (r) { return (r.response.ok ? r.getData() : r.response); })
            .then(function (r) { return r.entries; });
    };
    /**
     * Get details of a metadata object specified by its uri
     *
     * @param uri - uri of the metadata object for which details are to be retrieved
     * @returns object details
     */
    MetadataModule.prototype.getObjectDetails = function (uri) {
        return this.xhr.get(uri).then(function (r) { return r.getData(); });
    };
    /**
     * Get folders with items.
     * Returns array of folders, each having a title and items property which is an array of
     * corresponding items. Each item is either a metric or attribute, keeping its original
     * verbose structure.
     *
     * @param type - type of folders to return
     * @returns Array of folder object, each containing title and
     * corresponding items.
     */
    MetadataModule.prototype.getFoldersWithItems = function (projectId, type) {
        var _this = this;
        // fetch all folders of given type and process them
        return this.getFolders(projectId, type).then(function (folders) {
            // Helper public to get details for each metric in the given
            // array of links to the metadata objects representing the metrics.
            // @returns the array of promises
            var getMetricItemsDetails = function (array) {
                return Promise.all(array.map(_this.getObjectDetails)).then(function (metricArgs) {
                    return metricArgs.map(function (item) { return item.metric; });
                });
            };
            // helper mapBy function
            function mapBy(array, key) {
                return array.map(function (item) {
                    return item[key];
                });
            }
            // helper for sorting folder tree structure
            // sadly @returns void (sorting == mutating array in js)
            var sortFolderTree = function (structure) {
                structure.forEach(function (folder) {
                    folder.items.sort(function (a, b) {
                        if (a.meta.title < b.meta.title) {
                            return -1;
                        }
                        else if (a.meta.title > b.meta.title) {
                            return 1;
                        }
                        return 0;
                    });
                });
                structure.sort(function (a, b) {
                    if (a.title < b.title) {
                        return -1;
                    }
                    else if (a.title > b.title) {
                        return 1;
                    }
                    return 0;
                });
            };
            var foldersLinks = mapBy(folders, "link");
            var foldersTitles = mapBy(folders, "title");
            // fetch details for each folder
            return Promise.all(foldersLinks.map(_this.getObjectDetails)).then(function (folderDetails) {
                // if attribute, just parse everything from what we've received
                // and resolve. For metrics, lookup again each metric to get its
                // identifier. If passing unsupported type, reject immediately.
                if (type === "attribute") {
                    // get all attributes, subtract what we have and add rest in unsorted folder
                    return _this.getAttributes(projectId).then(function (attributes) {
                        // get uris of attributes which are in some dimension folders
                        var attributesInFolders = [];
                        folderDetails.forEach(function (fd) {
                            fd.dimension.content.attributes.forEach(function (attr) {
                                attributesInFolders.push(attr.meta.uri);
                            });
                        });
                        // unsortedUris now contains uris of all attributes which aren't in a folder
                        var unsortedUris = attributes
                            .filter(function (item) { return attributesInFolders.indexOf(item.link) === -1; })
                            .map(function (item) { return item.link; });
                        // now get details of attributes in no folders
                        return Promise.all(unsortedUris.map(_this.getObjectDetails)).then(function (unsortedAttributeArgs) {
                            // TODO add map to r.json
                            // get unsorted attribute objects
                            var unsortedAttributes = unsortedAttributeArgs.map(function (attr) { return attr.attribute; });
                            // create structure of folders with attributes
                            var structure = folderDetails.map(function (folderDetail) {
                                return {
                                    title: folderDetail.dimension.meta.title,
                                    items: folderDetail.dimension.content.attributes,
                                };
                            });
                            // and append "Unsorted" folder with attributes to the structure
                            structure.push({
                                title: "Unsorted",
                                items: unsortedAttributes,
                            });
                            sortFolderTree(structure);
                            return structure;
                        });
                    });
                }
                else if (type === "metric") {
                    var entriesLinks_1 = folderDetails.map(function (entry) {
                        return mapBy(entry.folder.content.entries, "link");
                    });
                    // get all metrics, subtract what we have and add rest in unsorted folder
                    return _this.getMetrics(projectId).then(function (metrics) {
                        // get uris of metrics which are in some dimension folders
                        var metricsInFolders = [];
                        folderDetails.forEach(function (fd) {
                            fd.folder.content.entries.forEach(function (metric) {
                                metricsInFolders.push(metric.link);
                            });
                        });
                        // unsortedUris now contains uris of all metrics which aren't in a folder
                        var unsortedUris = metrics
                            .filter(function (item) { return metricsInFolders.indexOf(item.link) === -1; })
                            .map(function (item) { return item.link; });
                        // sadly order of parameters of concat matters! (we want unsorted last)
                        entriesLinks_1.push(unsortedUris);
                        // now get details of all metrics
                        return Promise.all(entriesLinks_1.map(function (linkArray) { return getMetricItemsDetails(linkArray); })).then(function (tree) {
                            // TODO add map to r.json
                            // all promises resolved, i.e. details for each metric are available
                            var structure = tree.map(function (treeItems, idx) {
                                // if idx is not in folders list than metric is in "Unsorted" folder
                                return {
                                    title: foldersTitles[idx] || "Unsorted",
                                    items: treeItems,
                                };
                            });
                            sortFolderTree(structure);
                            return structure;
                        });
                    });
                }
                return Promise.reject(null);
            });
        });
    };
    /**
     * Get identifier of a metadata object identified by its uri
     *
     * @param uri - uri of the metadata object for which the identifier is to be retrieved
     * @returns object identifier
     */
    MetadataModule.prototype.getObjectIdentifier = function (uri) {
        function idFinder(obj) {
            // TODO
            if (obj.attribute) {
                return obj.attribute.content.displayForms[0].meta.identifier;
            }
            else if (obj.dimension) {
                return obj.dimension.content.attributes.content.displayForms[0].meta.identifier;
            }
            else if (obj.metric) {
                return obj.metric.meta.identifier;
            }
            else if (obj.dataSet) {
                return obj.dataSet.meta.identifier;
            }
            throw Error("Unknown object!");
        }
        if (!isPlainObject_1.default(uri)) {
            return this.getObjectDetails(uri).then(function (data) { return idFinder(data); });
        }
        return Promise.resolve(idFinder(uri));
    };
    /**
     * Get uri of an metadata object, specified by its identifier and project id it belongs to
     *
     * @param projectId - id of the project
     * @param identifier - identifier of the metadata object
     * @returns uri of the metadata object
     */
    MetadataModule.prototype.getObjectUri = function (projectId, identifier) {
        return this.xhr
            .post("/gdc/md/" + projectId + "/identifiers", {
            body: {
                identifierToUri: [identifier],
            },
        })
            .then(function (r) {
            var data = r.getData();
            var found = data.identifiers.find(function (pair) { return pair.identifier === identifier; });
            if (found) {
                return found.uri;
            }
            // WARNING: code in sdk-backend-bear is searching for 'not found' within the message
            //  in order to determine the NotFound status. Cannot use the response status because
            //  the POST request returns 200
            throw new xhr_1.ApiResponseError("Object with identifier " + identifier + " not found in project " + projectId, r.response, r.responseBody);
        });
    };
    /**
     * Get uris specified by identifiers
     *
     * @param projectId - id of the project
     * @param identifiers - identifiers of the metadata objects
     * @returns array of identifier + uri pairs
     */
    MetadataModule.prototype.getUrisFromIdentifiers = function (projectId, identifiers) {
        if (!identifiers.length) {
            return Promise.resolve([]);
        }
        return this.xhr
            .post("/gdc/md/" + projectId + "/identifiers", {
            body: {
                identifierToUri: identifiers,
            },
        })
            .then(function (r) { return r.getData(); })
            .then(function (data) {
            return data.identifiers;
        });
    };
    /**
     * Get identifiers specified by uris
     *
     * @param projectId - id of the project
     * @param uris - of the metadata objects
     * @returns array of identifier + uri pairs
     */
    MetadataModule.prototype.getIdentifiersFromUris = function (projectId, uris) {
        return this.xhr
            .post("/gdc/md/" + projectId + "/identifiers", {
            body: {
                uriToIdentifier: uris,
            },
        })
            .then(function (r) { return r.getData(); })
            .then(function (data) {
            return data.identifiers;
        });
    };
    /**
     * Get attribute elements with their labels and uris.
     *
     * @param projectId - id of the project
     * @param labelUri - uri of the label (display form)
     * @param patterns - elements labels/titles (for EXACT mode), or patterns (for WILD mode)
     * @param mode - match mode, currently only EXACT supported
     * @returns array of elementLabelUri objects
     */
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    MetadataModule.prototype.translateElementLabelsToUris = function (projectId, labelUri, patterns, mode) {
        if (mode === void 0) { mode = "EXACT"; }
        return this.xhr
            .post("/gdc/md/" + projectId + "/labels", {
            body: {
                elementLabelToUri: [
                    {
                        labelUri: labelUri,
                        mode: mode,
                        patterns: patterns,
                    },
                ],
            },
        })
            .then(function (r) { var _a; return (r.response.ok ? (_a = r.getData()) === null || _a === void 0 ? void 0 : _a.elementLabelUri : r.response); });
    };
    /**
     * Get valid elements of an attribute, specified by its identifier and project id it belongs to
     *
     * @param projectId - id of the project
     * @param id - display form id of the metadata object
     * @param options - objects with options
     * @returns ValidElements response
     */
    MetadataModule.prototype.getValidElements = function (projectId, id, options) {
        var _this = this;
        if (options === void 0) { options = {}; }
        var query = pickBy_1.default(pick_1.default(options, ["limit", "offset", "order", "filter", "prompt"]), function (val) { return val !== undefined; });
        var queryParams = util_1.queryString(query);
        var pickedOptions = pick_1.default(options, [
            "uris",
            "complement",
            "includeTotalCountWithoutFilters",
            "restrictiveDefinition",
        ]);
        var afm = options.afm;
        var getRequestBodyWithReportDefinition = function () {
            return _this.xhr
                .post("/gdc/app/projects/" + projectId + "/executeAfm/debug", {
                body: {
                    execution: {
                        afm: afm,
                    },
                },
            })
                .then(function (response) { return response.getData(); })
                .then(function (reportDefinitionResult) { return (tslib_1.__assign(tslib_1.__assign({}, pickedOptions), { restrictiveDefinitionContent: reportDefinitionResult.reportDefinitionWithInlinedMetrics.content })); });
        };
        var getOptions = afm ? getRequestBodyWithReportDefinition : function () { return Promise.resolve(pickedOptions); };
        return getOptions().then(function (requestBody) {
            return _this.xhr.postParsed(("/gdc/md/" + projectId + "/obj/" + id + "/validElements" + queryParams).replace(/\?$/, ""), {
                body: {
                    validElementsRequest: requestBody,
                },
            });
        });
    };
    /**
     * Get visualization by Uri and process data
     *
     * @param uri - visualization URI
     */
    MetadataModule.prototype.getVisualization = function (uri) {
        return this.getObjectDetails(uri).then(function (visualizationObject) {
            var mdObject = visualizationObject.visualizationObject;
            return {
                visualizationObject: mdObject,
            };
        });
    };
    /**
     * Save visualization
     *
     * @param projectId - id of the project to save the visualization to
     * @param visualization - the visualization to save
     */
    MetadataModule.prototype.saveVisualization = function (projectId, visualization) {
        return this.createObject(projectId, { visualizationObject: visualization.visualizationObject });
    };
    /**
     * Update visualization
     *
     * @param projectId - id of the project to update the visualization in
     * @param visualization - the visualization to update
     */
    MetadataModule.prototype.updateVisualization = function (projectId, visualizationUri, visualization) {
        var objectId = visualizationUri.split("/").slice(-1)[0];
        return this.updateObject(projectId, objectId, {
            visualizationObject: visualization.visualizationObject,
        });
    };
    /**
     * Delete visualization
     *
     * @param visualizationUri - URI of the visualization to delete
     */
    MetadataModule.prototype.deleteVisualization = function (visualizationUri) {
        return this.deleteObject(visualizationUri);
    };
    /**
     * Delete object
     *
     * @experimental
     * @param uri - of the object to be deleted
     */
    MetadataModule.prototype.deleteObject = function (uri) {
        return this.xhr.del(uri);
    };
    /**
     * Bulk delete objects
     */
    MetadataModule.prototype.bulkDeleteObjects = function (projectId, uris, mode) {
        if (mode === void 0) { mode = "cascade"; }
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var uri, data;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        uri = "/gdc/md/" + projectId + "/objects/delete";
                        data = {
                            delete: {
                                items: uris,
                                mode: mode,
                            },
                        };
                        return [4 /*yield*/, this.xhr.post(uri, { data: data })];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Create object
     *
     * @experimental
     * @param projectId - id of the project to create the object in
     * @param obj - object definition
     */
    MetadataModule.prototype.createObject = function (projectId, obj) {
        return this.xhr.postParsed("/gdc/md/" + projectId + "/obj?createAndGet=true", {
            body: obj,
        });
    };
    /**
     * Update object
     *
     * @experimental
     * @param projectId - id of the project to update the object in
     * @param objectId - objectId of the object to update
     * @param obj - object definition
     */
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    MetadataModule.prototype.updateObject = function (projectId, objectId, obj) {
        return this.xhr
            .put("/gdc/md/" + projectId + "/obj/" + objectId, {
            body: obj,
        })
            .then(function (r) { return r.getData(); });
    };
    /**
     * Converts the visualization object to legacy report.
     * @param projectId - id of the project to perform the conversion in
     * @param mdObject - visualization object to convert
     * @returns uri to the converted report
     */
    MetadataModule.prototype.openVisualizationAsReport = function (projectId, mdObject) {
        return this.xhr
            .post("/gdc/internal/projects/" + projectId + "/convertVisualizationObject", {
            body: mdObject,
        })
            .then(function (res) { return res.getData(); })
            .then(function (_a) {
            var uri = _a.uri;
            return uri;
        });
    };
    /**
     * LDM manage
     *
     * @experimental
     * @param projectId - id of the project to use
     * @param maql - MAQL to manage
     * @param options - for polling (maxAttempts, pollStep)
     */
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    MetadataModule.prototype.ldmManage = function (projectId, maql, options) {
        var _this = this;
        if (options === void 0) { options = {}; }
        return this.xhr
            .post("/gdc/md/" + projectId + "/ldm/manage2", { body: { manage: { maql: maql } } })
            .then(function (r) { return r.getData(); })
            .then(function (response) {
            var manageStatusUri = response.entries[0].link;
            return util_1.handlePolling(_this.xhr.get.bind(_this.xhr), manageStatusUri, _this.isTaskFinished, options);
        })
            .then(this.checkStatusForError);
    };
    /**
     * ETL pull
     *
     * @experimental
     * @param projectId - id of the project to use
     * @param uploadsDir - the directory to use
     * @param options - for polling (maxAttempts, pollStep)
     */
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    MetadataModule.prototype.etlPull = function (projectId, uploadsDir, options) {
        var _this = this;
        if (options === void 0) { options = {}; }
        return this.xhr
            .post("/gdc/md/" + projectId + "/etl/pull2", { body: { pullIntegration: uploadsDir } })
            .then(function (r) { return r.getData(); })
            .then(function (response) {
            var etlPullStatusUri = response.pull2Task.links.poll;
            return util_1.handlePolling(_this.xhr.get.bind(_this.xhr), etlPullStatusUri, _this.isTaskFinished, options);
        })
            .then(this.checkStatusForError);
    };
    MetadataModule.prototype.isTaskFinished = function (task) {
        var taskState = task.wTaskStatus.status;
        return taskState === "OK" || taskState === "ERROR";
    };
    MetadataModule.prototype.checkStatusForError = function (response) {
        if (response.wTaskStatus.status === "ERROR") {
            return Promise.reject(response);
        }
        return response;
    };
    return MetadataModule;
}());
exports.MetadataModule = MetadataModule;
//# sourceMappingURL=metadata.js.map